import { useEffect, useMemo, useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { Drawer } from "../Drawer";
import { Icon } from "../ui/Icon";
import { SearchSelect, type SSOption } from "../ui/SearchSelect";
import { PaymentTermsFields, emptyPaymentTerms, paymentTermsToInput, type PaymentTerms } from "../purchasing/PaymentTermsFields";
import { trpc } from "../../lib/trpc";

function parseTrNumber(raw: string): number {
  const s = raw.trim();
  if (/^\d{1,3}(\.\d{3})+(,\d+)?$/.test(s)) return Number(s.replace(/\./g, "").replace(",", "."));
  if (s.includes(",")) return Number(s.replace(/\./g, "").replace(",", "."));
  return Number(s);
}

type LineKind = "stock" | "service";
interface Row { key: string; kind: LineKind; itemId: string; description: string; qtyStr: string; priceStr: string }

let rowSeq = 0;
const newRow = (kind: LineKind = "stock"): Row => ({ key: `r${++rowSeq}`, kind, itemId: "", description: "", qtyStr: "", priceStr: "" });

/**
 * Bakım/onarım emrine BAĞLI satın alma talebi — çok satırlı (yedek parça: stok kartı; hizmet: açıklama+tutar).
 * createPosFromSuggestions(maintenanceOrderId) ile taslak PO açar; normal teklif→onay→teslim prosedürü izlenir.
 * PurchaseRequestDrawer deseninden; tedarikçi opsiyonel (RFQ), ödeme vadesi, talep eden.
 */
export function MaintenancePurchaseRequestDrawer({ open, maintenanceOrderId, orderCode, orderActive, onClose, onDone }: {
  open: boolean;
  maintenanceOrderId: string | null;
  orderCode: string | null;
  orderActive: boolean;
  onClose: () => void;
  onDone: (msg: string) => void;
}) {
  const qc = useQueryClient();
  const [rows, setRows] = useState<Row[]>([newRow()]);
  const [supplierId, setSupplierId] = useState("");
  const [requesterId, setRequesterId] = useState("");
  const [setWaiting, setSetWaiting] = useState(true);
  const [pt, setPt] = useState<PaymentTerms>(emptyPaymentTerms);
  const [err, setErr] = useState<string | null>(null);
  const [donePo, setDonePo] = useState<{ code: string; id: string } | null>(null);

  const itemOptsQ = trpc.inventory.itemOptions.useQuery(undefined, { enabled: open, staleTime: 60_000 });
  const suppliersQ = trpc.supplier.list.useQuery({ page: 1, pageSize: 100, isActive: true }, { enabled: open, staleTime: 60_000 });
  const usersQ = trpc.purchasing.assignableUsers.useQuery(undefined, { enabled: open, staleTime: 60_000 });
  const meQ = trpc.auth.me.useQuery(undefined, { staleTime: 5 * 60_000 });
  const createMut = trpc.purchasing.createPosFromSuggestions.useMutation();
  const setStatusMut = trpc.maintenance.order.setStatus.useMutation();

  useEffect(() => {
    if (open) { setRows([newRow()]); setSupplierId(""); setRequesterId(""); setSetWaiting(true); setPt(emptyPaymentTerms); setErr(null); setDonePo(null); createMut.reset(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, maintenanceOrderId]);

  const myId = meQ.data?.user?.id ?? null;
  useEffect(() => { if (open && myId) setRequesterId((cur) => cur || myId); }, [open, myId]);

  const stockOptions = useMemo<SSOption[]>(() =>
    ((itemOptsQ.data ?? []) as Array<{ id: string; kind: string; code: string; name: string }>)
      .filter((r) => r.kind === "stock")
      .map((r) => ({ value: r.id, label: r.name, tail: r.code })), [itemOptsQ.data]);
  const supplierOptions = useMemo<SSOption[]>(() => (suppliersQ.data?.rows ?? []).map((s) => ({ value: s.id, label: s.name, tail: s.code })), [suppliersQ.data]);
  const userOptions = useMemo<SSOption[]>(() => (usersQ.data ?? []).map((u) => ({ value: u.id, label: u.name })), [usersQ.data]);

  const patch = (key: string, p: Partial<Row>) => setRows((rs) => rs.map((r) => (r.key === key ? { ...r, ...p } : r)));

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setErr(null);
    if (!maintenanceOrderId) return;
    const items: Array<{ kind: LineKind; itemId?: string; description?: string; qty: number; supplierId: string | null; unitPrice?: number }> = [];
    for (const r of rows) {
      const n = r.qtyStr.trim() ? parseTrNumber(r.qtyStr) : NaN;
      if (!Number.isFinite(n) || n <= 0) return setErr("Her satırda geçerli bir miktar girin.");
      let unitPrice: number | undefined;
      if (r.priceStr.trim()) {
        const up = parseTrNumber(r.priceStr);
        if (!Number.isFinite(up) || up < 0) return setErr("Geçerli bir birim fiyat girin (TR biçimi: 12,50).");
        unitPrice = up;
      }
      if (r.kind === "stock") {
        if (!r.itemId) return setErr("Yedek parça satırında stok kartı seçin.");
        items.push({ kind: "stock", itemId: r.itemId, qty: n, supplierId: supplierId || null, unitPrice });
      } else {
        if (!r.description.trim()) return setErr("Hizmet satırında açıklama girin.");
        items.push({ kind: "service", description: r.description.trim(), qty: n, supplierId: supplierId || null, unitPrice });
      }
    }
    const pin = paymentTermsToInput(pt);
    try {
      const res = await createMut.mutateAsync({ items, maintenanceOrderId, requestedById: requesterId || undefined, ...pin });
      // Opsiyonel: bakım emrini "Parça Bekliyor"a al (aktifse). Hata olsa da PO oluştu.
      if (setWaiting && orderActive) {
        try { await setStatusMut.mutateAsync({ id: maintenanceOrderId, status: "waiting_parts" }); } catch { /* yoksay */ }
      }
      await qc.invalidateQueries();
      const c = res.created[0];
      setDonePo({ code: c?.code ?? "PO", id: c?.id ?? "" });
      onDone(`${c?.code ?? "PO"} taslak satın alma talebi oluşturuldu${orderCode ? ` (${orderCode})` : ""}.`);
    } catch { /* banner */ }
  }

  return (
    <Drawer
      open={open}
      onClose={onClose}
      size="xxl"
      title="Satın Alma Talebi — Bakım/Onarım"
      subtitle={orderCode ? `${orderCode} · yedek parça / hizmet` : undefined}
      footer={donePo ? (
        <>
          <button type="button" className="btn-md secondary" onClick={() => window.open(`/print/satinalma-talebi?id=${donePo.id}`, "_blank", "noopener")}>
            <Icon name="printer" size={14} />A4 Yazdır
          </button>
          <button type="button" className="btn-md primary" onClick={onClose}>Kapat</button>
        </>
      ) : (
        <>
          <button type="button" className="btn-md ghost" onClick={onClose} disabled={createMut.isPending}>İptal</button>
          <button type="submit" form="mpr-form" className="btn-md primary" disabled={createMut.isPending}>
            {createMut.isPending ? "Oluşturuluyor…" : "Taslak PO Oluştur"}
          </button>
        </>
      )}
    >
      {donePo ? (
        <div className="form-banner-ok">
          <div>
            <div style={{ fontWeight: 600, marginBottom: 6 }}>Talep oluşturuldu</div>
            <div>Taslak satın alma siparişi: <span className="mono" style={{ fontWeight: 600 }}>{donePo.code}</span>{orderCode ? <> · bağlı bakım: <span className="mono">{orderCode}</span></> : null}</div>
            <div style={{ marginTop: 6, fontSize: 12 }}>
              Onay, teklif ekleme ve mal kabul için <Link to="/purchasing" style={{ color: "var(--primary)" }}>Satınalma sayfasına</Link> gidin; ya da <b>A4 Yazdır</b> ile talebi çıktı alın.
            </div>
          </div>
        </div>
      ) : (
        <form id="mpr-form" onSubmit={handleSubmit} noValidate>
          {err || createMut.error ? <div className="form-banner-err" role="alert">{err ?? createMut.error?.message}</div> : null}

          {/* Talep satırları (yedek parça / hizmet) */}
          <div className="mpr-lines">
            {rows.map((r) => (
              <div className="mpr-line" key={r.key}>
                <select className="form-select mpr-kind" value={r.kind} onChange={(e) => patch(r.key, { kind: e.target.value as LineKind, itemId: "", description: "" })}>
                  <option value="stock">Yedek Parça</option>
                  <option value="service">Hizmet</option>
                </select>
                <div className="mpr-main">
                  {r.kind === "stock" ? (
                    <SearchSelect value={r.itemId || null} options={stockOptions} onChange={(v) => patch(r.key, { itemId: v ?? "" })}
                      placeholder="Stok kartı (yedek parça) seç…" searchPlaceholder="Parça ara…" icon="boxes" />
                  ) : (
                    <input className="form-input" type="text" value={r.description} maxLength={300} onChange={(e) => patch(r.key, { description: e.target.value })} placeholder="Hizmet açıklaması (ör. dış servis kalibrasyon)" />
                  )}
                </div>
                <input className="form-input mpr-qty" type="text" inputMode="decimal" value={r.qtyStr} onChange={(e) => patch(r.key, { qtyStr: e.target.value.replace(/[^\d.,]/g, "") })} placeholder="miktar" aria-label="Miktar" />
                <input className="form-input mpr-price" type="text" inputMode="decimal" value={r.priceStr} onChange={(e) => patch(r.key, { priceStr: e.target.value.replace(/[^\d.,]/g, "") })} placeholder="birim fiyat" aria-label="Birim fiyat" />
                <button type="button" className="iact danger" aria-label="Satırı kaldır" title="Satırı kaldır" disabled={rows.length === 1} onClick={() => setRows((rs) => rs.filter((x) => x.key !== r.key))}>
                  <Icon name="x" size={15} />
                </button>
              </div>
            ))}
          </div>
          <div style={{ display: "flex", gap: 8, margin: "8px 0 14px" }}>
            <button type="button" className="btn-md secondary" onClick={() => setRows((rs) => [...rs, newRow("stock")])}><Icon name="plus" size={14} />Yedek Parça</button>
            <button type="button" className="btn-md secondary" onClick={() => setRows((rs) => [...rs, newRow("service")])}><Icon name="plus" size={14} />Hizmet</button>
          </div>

          {/* Tedarikçi + talep eden */}
          <div className="po-fgrid">
            <div className="form-field">
              <label className="lbl" htmlFor="mpr-sup">Tedarikçi</label>
              <SearchSelect id="mpr-sup" value={supplierId || null} options={supplierOptions} onChange={(v) => setSupplierId(v ?? "")} placeholder="— Tedarikçi (opsiyonel, RFQ) —" searchPlaceholder="Tedarikçi ara…" clearable />
            </div>
            <div className="form-field">
              <label className="lbl" htmlFor="mpr-req">Talep Eden</label>
              <SearchSelect id="mpr-req" value={requesterId || null} options={userOptions} onChange={(v) => setRequesterId(v ?? "")} placeholder="— Talep eden —" searchPlaceholder="Kullanıcı ara…" />
            </div>
          </div>
          <span className="help" style={{ display: "block", marginBottom: 10 }}>
            Tedarikçi opsiyonel — boş bırakılırsa teklifler eklenip onaylanan seçilir. Hizmet satırları stoğa girmez (teslim = ifa edildi).
          </span>

          <PaymentTermsFields value={pt} onChange={setPt} enabled={open} />

          {orderActive ? (
            <label className="mpr-check">
              <input type="checkbox" checked={setWaiting} onChange={(e) => setSetWaiting(e.target.checked)} />
              Talep sonrası bakım emrini <b>"Parça Bekliyor"</b>a al
            </label>
          ) : null}

          <p style={{ fontSize: 12, color: "var(--neutral-500)", margin: "8px 0 0" }}>
            Talep, bu bakım emrine <b>bağlı</b> bir <b>taslak</b> PO olarak açılır; teklifler eklenip onaylanan seçilince tedarikçi + fiyat işlenir, teslim alınınca parça stoğa girer ve emirde kullanılabilir.
          </p>
        </form>
      )}
    </Drawer>
  );
}
